import { useCallback, useRef, useState } from 'react';
import { useWorkspaceScopedEffect } from './useWorkspaceScopedEffect';

type Options = {
  /** Whether visible data is already on screen (e.g. list length > 0). */
  hasData: () => boolean;
  /** Optional callback when workspace scope changes (clear selection state). */
  onWorkspaceChange?: () => void;
};

/**
 * Keeps existing UI visible during background polling refreshes.
 * Only shows loading state on the initial load or when no data is on screen yet.
 */
export function usePollingLoad({ hasData, onWorkspaceChange }: Options) {
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);
  const workspaceScopeInitializedRef = useRef(false);

  useWorkspaceScopedEffect(() => {
    if (!workspaceScopeInitializedRef.current) {
      workspaceScopeInitializedRef.current = true;
      return;
    }
    loadedRef.current = false;
    onWorkspaceChange?.();
  }, [onWorkspaceChange]);

  const run = useCallback(
    async (background: boolean, fetcher: () => Promise<void>) => {
      const keepVisible = background && (loadedRef.current || hasData());
      if (!keepVisible) {
        setLoading(true);
      }
      try {
        await fetcher();
        loadedRef.current = hasData();
      } finally {
        if (!keepVisible) {
          setLoading(false);
        }
      }
    },
    [hasData]
  );

  const initialLoading = loading && !loadedRef.current && !hasData();

  return { run, loading, initialLoading };
}
